import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, OctagonX, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, errText, fmtDateTime } from "./api";


function Funnel({ p }) {
  return (
    <span className="text-[13px] text-[#0F1825]" data-testid="batch-progress">
      campaign: <strong>{p.attempted}</strong> sent · <strong className="text-[#C84C21]">{p.bounced}</strong> bounced → <strong className="text-[#2E4A38]">{p.delivered}</strong> delivered · <strong>{p.approved_remaining}</strong> approved remaining · today {p.sent_today}/{p.daily_cap}
    </span>
  );
}

export function stageText(active, p) {
  if (!active) return "";
  if (active.status === "interrupted") return "Interrupted — resumes automatically when the server is back";
  if (active.cancel_requested) return "Stopping after the current email…";
  if (active.status === "sleeping") {
    const until = active.sleep_until ? fmtDateTime(active.sleep_until) : "soon";
    const r = active.sleep_reason || "";
    if (r === "daily limit reached") return `Daily limit reached (${p.sent_today}/${p.daily_cap}) — resumes ${until}`;
    if (r === "provider limit") return `Provider limit hit — paused until ${until}`;
    if (r === "daily start time") return `Waiting for today's start time — resumes ${until}`;
    return `Paused by you — until ${until}`;
  }
  const remaining = p.approved_remaining || 0;
  return `Sending ${p.this_run + 1} of ${p.this_run + remaining} · ${p.sent_today}/${p.daily_cap} today`;
}

export function BatchBanner({ batch, onChanged }) {
  const active = batch?.active;
  const last = batch?.last;
  const p = batch?.progress || {};
  const [busy, setBusy] = useState(false);
  const stop = async () => {
    if (!window.confirm("Stop sending? Remaining contacts stay approved; nothing restarts until you press Start sending again.")) return;
    setBusy(true);
    try { await api.batchStop(); toast.success("Stopped — remaining contacts stay approved; it will not restart on its own"); onChanged(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  if (active) {
    const planned = Math.max(p.planned || 0, (p.this_run || 0) + (p.approved_remaining || 0), 1);
    const pct = Math.min(100, Math.round(((p.this_run || 0) / planned) * 100));
    const paused = active.status === "sleeping";
    const tone = active.status === "interrupted" ? "bg-[#FDECE6] border-[#C84C21]" : paused ? "bg-[#FFF6E5] border-[#B7791F]" : "bg-[#EAF3EC] border-[#3F6D55]";
    return (
      <div className={`border-l-4 px-5 py-3 space-y-2 ${tone}`} data-testid="batch-banner">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0F1825]">
            {active.status === "interrupted" ? <AlertTriangle size={13} className="text-[#C84C21]" /> : <Loader2 size={13} className="animate-spin" />}
            {active.status === "interrupted" ? "Interrupted" : paused ? "Paused" : active.cancel_requested ? "Stopping…" : "Sending"}
            {paused && <span className="bg-[#C84C21] text-white px-1.5 py-0.5 tracking-[0.16em]" data-testid="paused-badge">paused</span>}
            {active.lease_stale && <span className="text-[#B7791F]" data-testid="lease-stale">· no heartbeat for {Math.round((active.lease_age_s || 0) / 60)} min — self-repairs within 5 min</span>}
          </span>
          <span className="text-[13px] text-[#0F1825]" data-testid="batch-stage">{stageText(active, p)}</span>
          <Funnel p={p} />
          <button type="button" onClick={stop} disabled={busy || active.cancel_requested} className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#C84C21] hover:text-[#0F1825] disabled:opacity-50" data-testid="batch-stop">
            <OctagonX size={13} /> Stop
          </button>
        </div>
        <div className="h-1.5 bg-white/70 overflow-hidden" data-testid="batch-progress-bar" title={`${p.this_run || 0} of ${planned} this run`}>
          <div className={`h-full transition-[width] duration-700 ${paused ? "bg-[#B7791F]" : "bg-[#3F6D55]"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }
  // Finished-batch summary: only for THIS campaign, never on a fresh one (0 sent and 0 bounced → hidden).
  if (!last || last.archived || (p.attempted === 0 && p.bounced === 0)) return null;
  const tone = last.status === "failed" ? "bg-[#FDECE6] border-[#C84C21]" : last.status === "stopped" ? "bg-[#FFF6E5] border-[#B7791F]" : "bg-[#F4F1EA] border-[#A09680]";
  return (
    <div className={`border-l-4 px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 ${tone}`} data-testid="batch-finished-banner">
      <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0F1825]">
        <CheckCircle2 size={13} /> Batch {last.status}{last.finished_at ? ` · ${new Date(last.finished_at).toLocaleString()}` : ""}
      </span>
      <Funnel p={p} />
      {last.last_error && <span className="text-[12px] text-[#C84C21]" data-testid="batch-last-error">{last.last_error}</span>}
    </div>
  );
}

export function useSyncPoller(initial, onDone) {
  const [sync, setSync] = useState(initial || null);
  const wasActive = useRef(!!initial?.active);
  useEffect(() => { setSync(initial || null); }, [initial]);
  useEffect(() => {
    if (!sync?.active) return undefined;
    const t = setInterval(async () => {
      try { setSync(await api.syncStatus()); } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [sync?.active]);
  useEffect(() => {
    const active = !!sync?.active;
    if (wasActive.current && !active && sync) onDone(sync);
    wasActive.current = active;
  }, [sync, onDone]);
  return [sync, setSync];
}

